import { useState } from 'react';

import { useAppConfig } from '@context/AppConfigContext.tsx';
import { useMiniAppSDK } from '@context/SDKConfigContext.tsx';
import { useSDKHandler } from '@features/method-tester/hooks/index.ts';
import fetchRequest from '@utils/fetchRequest.ts';

import { VERSION_0_3 } from '@/constant';
import { useJsBridgeVersion } from '@/context/JsBridgeVersionContext.tsx';
import { ERROR_CODE_MESSAGE_MAP } from '@/features/method-tester/constants';
import { OPERATION_KEY } from '@/features/method-tester/types/SDKCallResult.ts';
import { useFetchRequest } from '@/hooks';
import { Button } from '@asphalt-react/button';
import { Card } from '@asphalt-react/card';
import { Loader } from '@asphalt-react/loader';
import { Heading } from '@asphalt-react/typography';
import { GetAuthCodeResult } from '@gopay/miniapp-client';

/* Changes added here for fetchUser is just for the debugging purpose */
type User = {
  id: number;
  auth_token: string;
  gopay_account_id: string;
  created_at: Date;
  updated_at: Date;
};

type AuthTokenAPIResponse = {
  success: boolean;
  data: {
    auth_token: string;
    gopay_account_id: string;
  };
};

const fetchUser = (baseURL: string) => {
  return fetchRequest<User[]>(`${baseURL}/getAllRecords`, {
    method: 'GET',
  });
};

const fetchAuthToken = (baseURL: string, authCode: string) => {
  return fetchRequest<AuthTokenAPIResponse>(
    `${baseURL}/v1/miniverse/auth/token`,
    {
      method: 'POST',
      body: {
        auth_code: authCode,
      },
    }
  );
};

export const AuthCode = () => {
  const {
    isLoading,
    response: authCodeResponse,
    invokeSDKActionV0_3,
    invokeSDKActionV1,
  } = useSDKHandler<GetAuthCodeResult>();

  const sdk = useMiniAppSDK();
  const { version } = useJsBridgeVersion();
  const { data, error, request } = useFetchRequest<User[]>();
  const {
    data: authTokenData,
    error: authTokenError,
    request: requestAuthToken,
  } = useFetchRequest<AuthTokenAPIResponse>();
  const { VITE_BASE_API_URL: baseURL } = useAppConfig();

  const handleFetchUsers = async () => {
    await request(() => fetchUser(baseURL));
  };

  const [selectedErrorCode, setSelectedErrorCode] = useState<number | null>(
    null
  );

  const getAuthCode = async () => {
    try {
      const options = {
        ...(selectedErrorCode && {
          error: {
            operation_key: OPERATION_KEY.AUTHCODE,
            code: selectedErrorCode,
          },
        }),
      };
      if (version === VERSION_0_3) {
        await invokeSDKActionV0_3(() => sdk.getAuthCode(), options);
      } else {
        await invokeSDKActionV1('GPMiniAppAuth', 'getAuthCode', {});
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetSelection = () => setSelectedErrorCode(null);

  const handleFetchAuthToken = async () => {
    const authCode = authCodeResponse?.data?.auth_code;
    if (!authCode) {
      console.error('Please get the auth code first');
      return;
    }

    await requestAuthToken(() => fetchAuthToken(baseURL, authCode));
  };

  return (
    <Card outlined={true}>
      <div className="flex w-full justify-between">
        <Heading h6>Authcode</Heading>

        <Button size="xs" onClick={getAuthCode} disabled={isLoading}>
          Get Authcode
        </Button>
      </div>
      <div className="flex gap-4">
        {ERROR_CODE_MESSAGE_MAP.location.map(({ code, message }) => (
          <label key={code} className="flex items-center gap-1">
            <input
              type="radio"
              name="ERROR_CODE"
              value={code}
              checked={selectedErrorCode === code}
              onChange={() => setSelectedErrorCode(code)}
            />
            {message}
          </label>
        ))}
      </div>
      <Button size="xs" onClick={resetSelection}>
        Reset Error Code
      </Button>
      {isLoading && <Loader />}
      {!isLoading && authCodeResponse && (
        <div className="mt-2 w-full">
          <pre
            data-testid="auth-response"
            className="overflow-auto text-sm bg-gray-100 p-2 rounded"
          >
            {JSON.stringify(authCodeResponse, null, 2)}
          </pre>
        </div>
      )}
      {data && (
        <pre data-testid="fetch-users-response">{JSON.stringify(data)}</pre>
      )}
      {error && (
        <div data-testid="fetch-users-error">
          Error: {JSON.stringify(error)}
        </div>
      )}
      <Button data-testid="call-api-button" onClick={handleFetchUsers}>
        call API
      </Button>

      <Button
        data-testid="get-auth-token-button"
        onClick={handleFetchAuthToken}
      >
        get Auth Token
      </Button>

      {authTokenData && (
        <div className="mt-2 w-full">
          <pre
            data-testid="auth-token-response"
            className="overflow-auto text-sm bg-gray-100 p-2 rounded"
          >
            {JSON.stringify(authTokenData, null, 2)}
          </pre>
        </div>
      )}
      {authTokenError && (
        <div className="mt-2 w-full">
          <div
            data-testid="auth-token-error"
            className="overflow-auto text-sm bg-gray-100 p-2 rounded"
          >
            {JSON.stringify(authTokenError, null, 2)}
          </div>
        </div>
      )}
    </Card>
  );
};
